import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ClientWelcomeHeader from './components/ClientWelcomeHeader';
import TrainingPlanCard from './components/TrainingPlanCard';
import EvaluationModeCard from './components/EvaluationModeCard';
import PlanSelectionConfirmDialog from './components/PlanSelectionConfirmDialog';
import ClientProgressNav from '../../components/ui/ClientProgressNav';

const TrainingPlanSelection = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isEvaluationMode, setIsEvaluationMode] = useState(false);
  const [clientData] = useState({
    name: "Margaret Chen",
    id: "MC001",
    lastLogin: new Date(Date.now() - 86400000) // Yesterday
  });

  // Mock training plans data
  const [trainingPlans] = useState([
    {
      id: 1,
      name: "Senior Strength Foundation",
      description: "Build core strength with gentle, progressive exercises",
      currentPhase: "Foundation",
      progressPercentage: 65,
      completedExercises: 8,
      totalExercises: 12,
      estimatedDuration: 35,
      lastCompleted: new Date(Date.now() - 172800000), // 2 days ago
      status: "in-progress",
      canResume: true,
      difficulty: "Beginner",
      targetAreas: ["Core", "Balance", "Flexibility"]
    },
    {
      id: 2,
      name: "Balance & Mobility Plus",
      description: "Improve balance and maintain mobility for daily activities",
      currentPhase: "Strength",
      progressPercentage: 30,
      completedExercises: 4,
      totalExercises: 15,
      estimatedDuration: 40,
      lastCompleted: new Date(Date.now() - 604800000), // 1 week ago
      status: "not-started",
      canResume: false,
      difficulty: "Intermediate",
      targetAreas: ["Balance", "Mobility", "Coordination"]
    },
    {
      id: 3,
      name: "Gentle Cardio Recovery",
      description: "Low-impact cardiovascular exercises for heart health",
      currentPhase: "Recovery",
      progressPercentage: 90,
      completedExercises: 9,
      totalExercises: 10,
      estimatedDuration: 25,
      lastCompleted: new Date(Date.now() - 86400000), // Yesterday
      status: "completed",
      canResume: false,
      difficulty: "Beginner",
      targetAreas: ["Cardio", "Recovery", "Breathing"]
    }
  ]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setIsEvaluationMode(false);
    setShowConfirmDialog(true);
  };

  const handleEvaluationSelect = () => {
    setSelectedPlan(null);
    setIsEvaluationMode(true);
    setShowConfirmDialog(true);
  };

  const handleConfirmSelection = (plan) => {
    setShowConfirmDialog(false);
    
    if (isEvaluationMode) {
      // Navigate to evaluation dashboard (not implemented in this scope)
      console.log('Navigating to evaluation dashboard');
      // navigate('/evaluation-dashboard');
    } else {
      // Navigate to exercise execution with selected plan
      navigate('/exercise-execution', { 
        state: { 
          plan: plan,
          resumeSession: plan?.canResume || false
        }
      });
    }
  };

  const handleCancelSelection = () => {
    setShowConfirmDialog(false);
    setSelectedPlan(null);
    setIsEvaluationMode(false);
  };

  const handleLogout = () => {
    // Navigate back to authentication
    navigate('/');
  };

  // Auto-save user session data
  useEffect(() => {
    const saveSession = () => {
      localStorage.setItem('fittrainer_client_session', JSON.stringify({
        clientId: clientData?.id,
        lastAccess: new Date()?.toISOString(),
        selectedPlans: trainingPlans?.filter(plan => plan?.status === 'in-progress')?.map(plan => plan?.id)
      }));
    };

    const interval = setInterval(saveSession, 30000); // Save every 30 seconds
    return () => clearInterval(interval);
  }, [clientData?.id, trainingPlans]);

  return (
    <div className="min-h-screen bg-background">
      {/* Welcome Header */}
      <ClientWelcomeHeader 
        clientName={clientData?.name}
        onLogout={handleLogout}
      />
      {/* Main Content */}
      <main className="p-8 pb-32 touch-spacing">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Choose Your Training Plan
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Select a training plan to continue your fitness journey, or access evaluation mode to review your progress.
            </p>
          </div>

          {/* Training Plans Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-12">
            {trainingPlans?.map((plan) => (
              <TrainingPlanCard
                key={plan?.id}
                plan={plan}
                onSelect={handlePlanSelect}
                isSelected={selectedPlan?.id === plan?.id}
              />
            ))}
          </div>

          {/* Evaluation Mode Section */}
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-foreground mb-2">
                Track Your Progress
              </h3>
              <p className="text-lg text-muted-foreground">
                Review your fitness journey and see how far you've come
              </p>
            </div>
            
            <EvaluationModeCard onSelect={handleEvaluationSelect} />
          </div>

          {/* Quick Tips */}
          <div className="mt-16 bg-surface rounded-2xl p-8 border border-border">
            <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
              Today's Fitness Tips
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💧</span>
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Stay Hydrated</h4>
                <p className="text-muted-foreground">Drink water before, during, and after your workout</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🧘</span>
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Listen to Your Body</h4>
                <p className="text-muted-foreground">Take breaks when needed and never push through pain</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Consistency Wins</h4>
                <p className="text-muted-foreground">Regular, gentle exercise is better than intense, sporadic workouts</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Confirmation Dialog */}
      <PlanSelectionConfirmDialog
        isOpen={showConfirmDialog}
        plan={selectedPlan}
        isEvaluationMode={isEvaluationMode}
        onConfirm={handleConfirmSelection}
        onCancel={handleCancelSelection}
      />
      {/* Bottom Navigation */}
      <ClientProgressNav 
        onBack={() => {}}
        onForward={() => {}}
        onPause={() => {}}
        onEmergency={() => {}}
      />
    </div>
  );
};

export default TrainingPlanSelection;